export const PROJECT_TYPES = [
  "web",
  "mobile",
  "backend",
  "full-stack web",
  "full-stack mobile",
  "full-stack",
];
// Timelines run in 4-week blocks — one per month — out to 28 months (112w).
// "6w" is kept as an off-grid option because it's the house standard.
export const TIMELINE_MAX_MONTHS = 28;
const MONTH_TIMELINES = Array.from(
  { length: TIMELINE_MAX_MONTHS },
  (_, i) => `${(i + 1) * 4}w`,
);
export const TIMELINES = ["4w", "6w", ...MONTH_TIMELINES.slice(1)];
export const PAYMENT_PLANS = ["full", "lease", "rent-to-own"];
export const PROJECT_STATUSES = [
  "discovery",
  "design",
  "build",
  "review",
  "launched",
  "on-hold",
];

// Legacy timeline-estimator rates. The curve ramps 4w → 12w and then plateaus:
// every timeline of 12 weeks or longer prices at the same ceiling rate.
const RATE_TIMELINE_CEILING = 158; // 12w and beyond
const RATE_BY_TIMELINE = {
  "4w": 68,
  "6w": 113,
  "8w": 135,
  ...Object.fromEntries(
    MONTH_TIMELINES.slice(2).map((t) => [t, RATE_TIMELINE_CEILING]),
  ),
};
// AI-era calibration (2026): AI-accelerated workflows roughly halved build
// hours, so a standard build lands around 80H instead of 180H.
// MUST stay in sync with fw_mobile/src/features/request/pricing.ts.
const BASE_HOURS_BY_TYPE = {
  mobile: 120,
  shop: 80,
  backend: 90,
  web: 50,
  brand: 55,
};
const HOURS_PER_SCOPE_ITEM = 4;
const MIN_HOURS = 40; // legacy timeline-estimator floor

// ─── Discipline-based pricing (mirror of fw_mobile pricing.ts) ───────────────
// Rate is driven by the kind of work, not the timeline:
//   • front-end only / back-end only → $60/hr
//   • front-end + back-end (full-stack) → $120/hr
// Every request floors at REQUEST_MIN_HOURS. Back-end = backend | ai.
// ("API work" was removed — it's the same as back-end work.)
export const DISCIPLINES = ["frontend", "backend", "ai"];
export const DISCIPLINE_LABELS = {
  frontend: "Frontend",
  backend: "Backend",
  ai: "AI integrations",
};
export const BACKEND_DISCIPLINES = ["backend", "ai"];
export const RATE_SINGLE_SIDE = 60;
export const RATE_FULL_STACK = 120;
export const REQUEST_MIN_HOURS = 80;

// ─── Turnaround pricing (mirror of fw_mobile pricing.ts) ─────────────────────
// 6 weeks is the house standard (no uplift). A 4-week rush adds +25%; 12 weeks
// is standard too.
export const STANDARD_TIMELINE = "6w";
export const RUSH_TIMELINE = "4w";
export const RUSH_UPLIFT = 0.25; // +25% on a 4-week rush turnaround

export const rushMultiplier = (timeline) =>
  timeline === RUSH_TIMELINE ? 1 + RUSH_UPLIFT : 1;

export const timelineWeeks = (timeline) => Number.parseInt(timeline, 10) || 0;

// "4w" → "4 weeks · rush", "6w" → "6 weeks · standard", "16w" → "16 weeks · 4 months"
export const timelineLabel = (timeline) => {
  const weeks = timelineWeeks(timeline);
  if (!weeks) return timeline;
  const months = weeks / 4;
  let suffix = "";
  if (timeline === RUSH_TIMELINE) suffix = " · rush";
  else if (timeline === STANDARD_TIMELINE) suffix = " · standard";
  else if (Number.isInteger(months))
    suffix = ` · ${months} month${months === 1 ? "" : "s"}`;
  return `${weeks} weeks${suffix}`;
};

export const rateForDisciplines = (disciplines = []) => {
  const hasFrontend = disciplines.includes("frontend");
  const hasBackend = disciplines.some((d) => BACKEND_DISCIPLINES.includes(d));
  return hasFrontend && hasBackend ? RATE_FULL_STACK : RATE_SINGLE_SIDE;
};

// Price a request from multi-select build types + disciplines + scope +
// turnaround. A 4-week rush multiplies the subtotal by 1.25.
export const computeRequestQuote = ({
  types = [],
  disciplines = [],
  scope = [],
  timeline = STANDARD_TIMELINE,
}) => {
  const baseHours = types.reduce(
    (sum, t) => sum + (BASE_HOURS_BY_TYPE[t] ?? 0),
    0,
  );
  const hours = Math.max(
    REQUEST_MIN_HOURS,
    baseHours + HOURS_PER_SCOPE_ITEM * scope.length,
  );
  const rate = rateForDisciplines(disciplines);
  const subtotal = hours * rate;
  const mult = rushMultiplier(timeline);
  const budgetUsd = Math.round(subtotal * mult);
  return {
    hours,
    rate,
    subtotalUsd: subtotal,
    rushMultiplier: mult,
    rushUpliftUsd: budgetUsd - subtotal,
    budgetUsd,
    fullStack: rate === RATE_FULL_STACK,
  };
};

// Overage terms — mirror of fw_mobile pricing.ts. Past the threshold, work
// is quoted as a fixed flat rate, or billed hourly at the overage rate.
export const OVERAGE_THRESHOLD_HOURS = 80;
export const OVERAGE_HOURLY_RATE = 120;
export const OVERAGE_NOTE = `Builds past ${OVERAGE_THRESHOLD_HOURS} hours convert to a fixed flat-rate quote — or hourly at $${OVERAGE_HOURLY_RATE}/h if you'd rather stay flexible. Locked in writing before anything bills.`;

export const computeQuote = ({ type, timeline, scope = [] }) => {
  const base = BASE_HOURS_BY_TYPE[type] ?? MIN_HOURS;
  const hours = Math.max(MIN_HOURS, base + HOURS_PER_SCOPE_ITEM * scope.length);
  const rate = RATE_BY_TIMELINE[timeline] ?? RATE_BY_TIMELINE["6w"];
  return { hours, rate, budgetUsd: hours * rate };
};

// Returns the milestones to seed for a given payment plan + budget.
// Each item has { label, pct, amount, order }. Caller supplies dueAt if any.
export const seedMilestonesFor = ({ paymentPlan, budgetUsd }) => {
  const round = (n) => Math.round(n);

  if (paymentPlan === "full") {
    return [
      { label: "Kickoff", pct: 0.5, amount: round(budgetUsd * 0.5), order: 0 },
      {
        label: "Build midpoint",
        pct: 0.3,
        amount: round(budgetUsd * 0.3),
        order: 1,
      },
      { label: "Launch", pct: 0.2, amount: round(budgetUsd * 0.2), order: 2 },
    ];
  }
  if (paymentPlan === "lease") {
    const pct = 1 / 12;
    return Array.from({ length: 12 }, (_, i) => ({
      label: `Lease · month ${i + 1}`,
      pct,
      amount: round(budgetUsd * pct),
      order: i,
    }));
  }
  if (paymentPlan === "rent-to-own") {
    const pct = 1 / 24;
    return Array.from({ length: 24 }, (_, i) => ({
      label: `Rent-to-own · month ${i + 1}`,
      pct,
      amount: round(budgetUsd * pct),
      order: i,
    }));
  }
  return [];
};
